/** 국가 — where the money comes from, and whether it leans on one market (S4).
 *
 *  `/api/report/stats?by=country` over a period (`?range=`, as 앱): the top
 *  five countries and the rest as one bar, the share the top five hold, and
 *  every country in a table. (The old page read `/api/countries`, a fixed
 *  30 days of the same figures; that endpoint stays but no screen uses it.)
 *  An empty code is traffic AdMob could not place: 미상. */

import { useEffect, useMemo, useState } from 'react'
import type { StatsRow } from '@/api/client'
import { countryName } from '@/lib/country'
import { count, percent, usd } from '@/lib/money'
import { useStats } from '@/lib/queries'
import {
  CountryTag,
  DataTable,
  EmptyState,
  FilterBar,
  Legend,
  MiniBar,
  Money,
  PageHeader,
  Pager,
  RangeFilter,
  StackedBar,
  Won,
  shareSegments,
  useRange,
  type Column,
} from '@/ui'

const PAGE_SIZE = 50

/** The top five countries (largest first) and their share of the period's
 *  revenue, as a fraction; null when nothing was earned. */
export function topShare(rows: StatsRow[], total: number): number | null {
  if (!(total > 0)) return null
  const top = [...rows].sort((a, b) => b.revenue - a.revenue).slice(0, 5)
  return top.reduce((s, r) => s + r.revenue, 0) / total
}

export default function Countries() {
  const { range, setRange, from, to, label } = useRange('30d')
  const q = useStats({ by: 'country', from, to })
  const [page, setPage] = useState(1)
  useEffect(() => setPage(1), [range])

  // Largest first, so the rank is the position whatever order the server sent.
  const rows = useMemo(() => (q.data ? [...q.data.rows].sort((a, b) => b.revenue - a.revenue) : undefined), [q.data])
  const total = q.data?.totals.revenue ?? 0
  const segments = useMemo(
    () => shareSegments((rows ?? []).map((r) => ({ key: r.country ?? '', label: countryName(r.country), value: r.revenue })), 5),
    [rows],
  )
  const share = rows ? topShare(rows, total) : null
  const pageRows = rows?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const rankOf = (r: StatsRow) => (rows?.indexOf(r) ?? 0) + 1
  const shareOf = (r: StatsRow) => (total > 0 ? r.revenue / total : 0)

  const columns: Column<StatsRow>[] = [
    { key: 'rank', header: '순위', card: 'hidden', width: '56px', cell: (r) => <span className="tabular-nums text-muted-foreground">{rankOf(r)}</span> },
    {
      key: 'country',
      header: '국가',
      card: 'title',
      cell: (r) => (
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium">{countryName(r.country)}</span>
          <CountryTag code={r.country ?? ''} />
        </span>
      ),
    },
    { key: 'revenue', header: '수익', align: 'right', card: 'hidden', cell: (r) => <Money value={r.revenue} krw /> },
    {
      key: 'share',
      header: '비중',
      card: 'meta',
      cell: (r) => <MiniBar value={shareOf(r) * 100} label="비중" text={percent(shareOf(r))} />,
    },
    { key: 'impressions', header: '노출', align: 'right', card: 'hidden', cell: (r) => <span className="tabular-nums">{count(r.impressions)}</span> },
    { key: 'clicks', header: '클릭', align: 'right', card: 'hidden', cell: (r) => <span className="tabular-nums">{count(r.clicks)}</span> },
    { key: 'ecpm', header: 'eCPM', align: 'right', card: 'hidden', cell: (r) => <Money value={r.ecpm} /> },
    { key: 'ctr', header: 'CTR', align: 'right', card: 'hidden', cell: (r) => <span className="tabular-nums">{r.impressions > 0 ? percent(r.ctr) : '—'}</span> },
    { key: 'match', header: '매칭률', align: 'right', card: 'hidden', cell: (r) => <span className="tabular-nums">{r.ad_requests > 0 ? percent(r.match_rate) : '—'}</span> },
    {
      key: 'summary',
      header: '수익 · eCPM',
      cardOnly: true,
      card: 'sub',
      cell: (r) => (
        <span className="tabular-nums">
          수익 <span className="whitespace-nowrap text-foreground">{usd(r.revenue)}</span><Won usd={r.revenue} className="ml-1" />
          {' · '}eCPM <span className="whitespace-nowrap text-foreground">{usd(r.ecpm)}</span>
        </span>
      ),
    },
  ]

  return (
    <>
      <PageHeader title="국가" description="AdMob 수익을 국가별로 본다. 광고비는 국가별로 나뉘지 않는다.">
        <FilterBar>
          <RangeFilter range={range} onChange={setRange} />
        </FilterBar>
      </PageHeader>

      {rows && rows.length > 0 && (
        <section aria-label="상위 5개국 비중" className="mb-4 rounded-lg border border-border bg-card p-4">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <h2 className="text-section font-semibold">
              {label}
              <span className="ml-2 text-meta font-normal text-muted-foreground">수익 {usd(total)}</span>
            </h2>
            <p className="text-table text-muted-foreground">
              {share === null ? (
                '수익이 없다'
              ) : (
                <>
                  상위 5가 전체의 <span className="font-semibold tabular-nums text-foreground">{percent(share)}</span>
                </>
              )}
            </p>
          </div>
          <StackedBar segments={segments} height={12} format={usd} />
          <Legend
            className="mt-3"
            items={segments.map((s) => ({
              label: s.label,
              swatch: s.fill,
              value: total > 0 ? percent(s.value / total) : undefined,
            }))}
          />
        </section>
      )}

      <div className="space-y-3">
        <DataTable
          label="국가별 수익"
          columns={columns}
          rows={pageRows}
          rowKey={(r) => r.country ?? ''}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => q.refetch()}
          empty={<EmptyState title={`${label} 동안 국가별 수익이 없다`} description="동기화가 끝나면 채워진다." />}
        />
        <Pager total={rows?.length ?? 0} page={page} pageSize={PAGE_SIZE} onPage={setPage} />
      </div>
    </>
  )
}
